import React from "react";
import { Link } from "react-router-dom";
import LegalLayout, { Section } from "./LegalLayout";

export default function PrivacyPage() {
  return (
    <LegalLayout title="Privacy Policy" updated="February 15, 2026">
      <p>
        LifeGuide ("LifeGuide," "we," "our," or "us") respects your privacy.
        This Privacy Policy describes how we collect, use, and protect your
        information when you use the LifeGuide website, mobile web app, and
        related services (collectively, the "Service").
      </p>

      <Section heading="1. Information We Collect">
        <p>We collect information you provide directly, including:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Account details (name, email address, password hash).</li>
          <li>Subscription and billing information processed by Stripe.</li>
          <li>Gift purchase details, including a recipient's email and phone number when you choose to gift LifeGuide access.</li>
          <li>Content you submit to the Service (journal entries, chat messages with the AI Life Coach, text you paste into the Relationship / Text Analyzer, Bible study prompts, and reflections).</li>
        </ul>
        <p>
          We also collect limited technical information automatically, such as
          device type, browser, IP address, approximate location, and usage
          events used to improve the Service.
        </p>
      </Section>

      <Section heading="2. How We Use Your Information">
        <ul className="list-disc pl-6 space-y-1">
          <li>To provide and personalize the Service, including AI-generated devotionals, coaching responses, and audio.</li>
          <li>To process payments and manage subscriptions or gifts.</li>
          <li>To deliver transactional communications, including account emails and, if you opt in, SMS messages about a gift you received.</li>
          <li>To improve, secure, and support the Service.</li>
          <li>To comply with law and enforce our Terms of Service.</li>
        </ul>
      </Section>

      <Section heading="3. SMS / Text Messaging">
        <p>
          Users may voluntarily and affirmatively opt in to receive SMS/text
          messages from LifeGuide and/or I Need a Proverb. Depending on the
          specific SMS feature you opt into, messages may include requested
          proverb or wisdom content, account and service notifications,
          LifeGuide reminders, gift-related communications, and other
          communications you specifically request or consent to receive.
          LifeGuide uses Twilio, Inc. to deliver these messages.
        </p>
        <p>
          <strong>LifeGuide's SMS program is informational and service-only.
          </strong>{" "}
          It does not include marketing or promotional SMS/text messages,
          and SMS consent collected by LifeGuide is not combined with any
          marketing/promotional consent. If LifeGuide ever offers
          marketing/promotional SMS, it will be through a separate,
          clearly-labelled affirmative opt-in.
        </p>
        <p>
          Message frequency varies. Message and data rates may apply. Reply{" "}
          <strong>STOP</strong> at any time to opt out of future SMS messages,
          and reply <strong>HELP</strong> for assistance. SMS consent is
          voluntary and is <em>not</em> a condition of purchasing any product
          or of using the basic LifeGuide service.
        </p>
        <p>
          <strong>
            We do not sell, rent, or share mobile phone numbers, SMS opt-in
            data, or SMS consent information with third parties or affiliates
            for their marketing or promotional purposes. SMS opt-in and
            consent information will not be shared with third parties for
            marketing purposes.
          </strong>{" "}
          We provide only the minimum information necessary to service
          providers strictly to deliver the communications you requested (for
          example, Twilio as our messaging carrier), subject to appropriate
          privacy and confidentiality obligations.
        </p>
        <p>
          <strong>Gifting and sharing.</strong> LifeGuide allows one person to
          share content with, or purchase a LifeGuide gift for, another
          person. A recipient does <em>not</em> consent to recurring SMS
          simply because someone else entered their phone number, sent them a
          proverb, or purchased them a LifeGuide gift. The recipient must
          provide their own affirmative consent before receiving ongoing or
          recurring informational/service SMS reminders from LifeGuide.
        </p>
        <p>
          Questions about our SMS communications? Email{" "}
          <a href="mailto:support@lifeguide.app" className="text-[#4F6F52] underline underline-offset-4">support@lifeguide.app</a>.
        </p>
      </Section>

      <Section heading="4. AI Processing">
        <p>
          To generate devotionals, coaching responses, and audio narration,
          LifeGuide sends prompts to third-party AI providers, including OpenAI
          and Anthropic. Do not include sensitive information (such as
          government IDs, financial account numbers, or medical records) in
          content submitted to the AI.
        </p>
      </Section>

      <Section heading="5. Sharing of Information">
        <p>We share information only with:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Service providers who process data on our behalf (Stripe for payments, Resend for email, Twilio for SMS, OpenAI/Anthropic for AI generation, MongoDB Atlas for hosting).</li>
          <li>Law enforcement or regulators when required by law.</li>
          <li>A successor entity in the event of a merger, acquisition, or sale of assets, under equivalent privacy protections.</li>
        </ul>
        <p>We do not sell your personal information.</p>
      </Section>

      <Section heading="6. Data Retention">
        <p>
          We retain your account and content for as long as your account is
          active. You may request deletion at any time by emailing
          support@lifeguide.app; we will delete your data within 30 days,
          except where retention is required by law (e.g., billing records).
        </p>
      </Section>

      <Section heading="7. Your Rights">
        <p>
          Depending on your location, you may have rights to access, correct,
          delete, or port your personal information, or to object to certain
          processing. To exercise these rights, email support@lifeguide.app.
        </p>
      </Section>

      <Section heading="8. Children">
        <p>
          LifeGuide is not directed to children under 13. We do not knowingly
          collect personal information from children under 13. If you believe a
          child has provided us information, please contact us for prompt
          deletion.
        </p>
      </Section>

      <Section heading="9. Security">
        <p>
          We use industry-standard safeguards including TLS encryption in
          transit, hashed passwords, and access controls. No system is
          perfectly secure; report suspected vulnerabilities to
          support@lifeguide.app.
        </p>
      </Section>

      <Section heading="10. Changes">
        <p>
          We may update this Privacy Policy from time to time. Material changes
          will be announced by email or in-app notice at least 7 days before
          they take effect.
        </p>
      </Section>

      <Section heading="11. Contact">
        <p>
          Questions? <Link to="/contact" className="text-[#4F6F52] underline underline-offset-4" data-testid="privacy-contact-link">Send us a message</Link>{" "}
          or email <a href="mailto:support@lifeguide.app" className="text-[#4F6F52] underline underline-offset-4">support@lifeguide.app</a>.
        </p>
      </Section>
    </LegalLayout>
  );
}
